import type { PersonDetail } from "@/api/db/people";
import type { SaveInput } from "@/api/router/people/schemas";
import { type } from "arktype";
import { maskCep, maskCpf } from "@/components/wizard/masks";
import { CadastroFields } from "@/lib/cadastro-fields";
import { djs } from "@/lib/djs";

const validCpf = type("string").narrow(
	(value, ctx) => CadastroFields.cpfError(value) === null || ctx.mustBe("a valid CPF"),
);
const validBirthDate = type("string").narrow(
	(value, ctx) => CadastroFields.birthDateError(value) === null || ctx.mustBe("a valid birth date"),
);
const consent = type("boolean | null");

export const CadastroFormSchemas = {
	draft: type({
		name: type("string").narrow(
			(value, ctx) => value.trim().length > 0 || ctx.mustBe("a non-empty name"),
		),
		cpf: validCpf,
		rg: "string",
		sex: "string",
		birth_date: validBirthDate,
		mother_name: "string",
		marital_status: "string",
		occupation: "string",
		has_insurance: consent,
		insurance: "string",
		preferred_channel: "string",
		how_found: "string",
		how_found_detail: "string",
		referred_by: "string",
		phones: "string[]",
		emails: "string[]",
		address_raw: "string",
		address_cep: "string",
		sms_marketing: consent,
		wa_marketing: consent,
		wa_reminder: consent,
		wa_birthday: consent,
		email_marketing: consent,
		email_reminder: consent,
		email_birthday: consent,
	}),
};

export type CadastroDraft = typeof CadastroFormSchemas.draft.infer;

export type ConsentField =
	| "sms_marketing"
	| "wa_marketing"
	| "wa_reminder"
	| "wa_birthday"
	| "email_marketing"
	| "email_reminder"
	| "email_birthday";

export const CONSENT_FIELDS: ConsentField[] = [
	"sms_marketing",
	"wa_marketing",
	"wa_reminder",
	"wa_birthday",
	"email_marketing",
	"email_reminder",
	"email_birthday",
];

export const emptyDraft: CadastroDraft = {
	name: "",
	cpf: "",
	rg: "",
	sex: "",
	birth_date: "",
	mother_name: "",
	marital_status: "",
	occupation: "",
	has_insurance: null,
	insurance: "",
	preferred_channel: "",
	how_found: "",
	how_found_detail: "",
	referred_by: "",
	phones: [""],
	emails: [""],
	address_raw: "",
	address_cep: "",
	sms_marketing: null,
	wa_marketing: null,
	wa_reminder: null,
	wa_birthday: null,
	email_marketing: null,
	email_reminder: null,
	email_birthday: null,
};

function draftFromPerson(person: PersonDetail): CadastroDraft {
	const primaryAddress = person.addresses.find((a) => a.primary) ?? person.addresses.at(0);
	const phones = person.phones.map((p) => p.raw).filter((p) => p !== "");
	const emails = person.emails.map((e) => e.email);

	return {
		name: person.name,
		cpf: person.cpf ?? "",
		rg: person.rg ?? "",
		sex: person.sex ?? "",
		birth_date: person.birth_date ? djs(person.birth_date).format("YYYY-MM-DD") : "",
		mother_name: person.mother_name ?? "",
		marital_status: person.marital_status ?? "",
		occupation: person.occupation ?? "",
		has_insurance: person.has_insurance,
		insurance: person.insurance ?? "",
		preferred_channel: person.preferred_channel ?? "",
		how_found: person.how_found ?? "",
		how_found_detail: person.how_found_detail ?? "",
		referred_by: person.referred_by ?? "",
		phones: phones.length > 0 ? phones : [""],
		emails: emails.length > 0 ? emails : [""],
		address_raw: primaryAddress ? (primaryAddress.raw ?? "") : "",
		address_cep: primaryAddress ? (primaryAddress.cep ?? "") : "",
		sms_marketing: person.sms_marketing,
		wa_marketing: person.wa_marketing,
		wa_reminder: person.wa_reminder,
		wa_birthday: person.wa_birthday,
		email_marketing: person.email_marketing,
		email_reminder: person.email_reminder,
		email_birthday: person.email_birthday,
	};
}

export function displayDraftFromPerson(person: PersonDetail): CadastroDraft {
	const base = draftFromPerson(person);

	return {
		...base,
		cpf: maskCpf(base.cpf),
		birth_date: CadastroFields.displayBirthDate(base.birth_date),
		address_cep: maskCep(base.address_cep),
		phones: base.phones.map(CadastroFields.displayPhone),
	};
}

function trimmed(value: string) {
	const next = value.trim();

	if (next === "") {
		return;
	}

	return next;
}

export function draftToInput(draft: CadastroDraft, id: number | undefined): SaveInput {
	const phones = draft.phones.map((p) => p.trim()).filter((p) => p !== "");
	const emails = draft.emails.map((e) => e.trim()).filter((e) => e !== "");
	const addressRaw = trimmed(draft.address_raw);
	const addressCep = trimmed(draft.address_cep);
	const insurance = draft.has_insurance === false ? undefined : trimmed(draft.insurance);

	return {
		id,
		name: draft.name.trim(),
		cpf: trimmed(draft.cpf),
		rg: trimmed(draft.rg),
		sex: trimmed(draft.sex),
		birth_date: trimmed(draft.birth_date),
		mother_name: trimmed(draft.mother_name),
		marital_status: trimmed(draft.marital_status),
		occupation: trimmed(draft.occupation),
		has_insurance: draft.has_insurance,
		insurance,
		preferred_channel: trimmed(draft.preferred_channel),
		how_found: trimmed(draft.how_found),
		how_found_detail: trimmed(draft.how_found_detail),
		referred_by: trimmed(draft.referred_by),
		phones: phones.length > 0 ? phones : undefined,
		emails: emails.length > 0 ? emails : undefined,
		address: addressRaw || addressCep ? { raw: addressRaw, cep: addressCep } : undefined,
		sms_marketing: draft.sms_marketing,
		wa_marketing: draft.wa_marketing,
		wa_reminder: draft.wa_reminder,
		wa_birthday: draft.wa_birthday,
		email_marketing: draft.email_marketing,
		email_reminder: draft.email_reminder,
		email_birthday: draft.email_birthday,
	};
}
